import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { formatApiError } from "@/lib/api";

export const AdminLogin = () => {
    const { user, login } = useAdminAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (user) return <Navigate to="/admin/dashboard" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await login(email.trim(), password);
            navigate("/admin/dashboard", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-login-page" className="min-h-screen bg-[#0A192F] flex items-center justify-center px-6">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-2.5 mb-8 justify-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-white text-[#0A192F] font-display font-semibold">A</span>
                    <div className="text-white">
                        <p className="font-display font-semibold text-lg leading-none">Aurea Digital Solutions</p>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-300 mt-1">Admin · Content panel</p>
                    </div>
                </div>

                <form onSubmit={submit} className="bg-white p-8 border border-slate-200">
                    <h1 className="font-display text-xl font-semibold text-slate-900">Sign in</h1>
                    <p className="text-[13px] text-slate-500 mt-1">Manage content for the public site.</p>

                    <div className="mt-6 space-y-4">
                        <label className="block">
                            <span className="block text-[12.5px] font-medium text-slate-700 mb-1.5">Email</span>
                            <input
                                type="email"
                                autoComplete="username"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="admin-login-email"
                                className="w-full border border-slate-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-teal-600"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[12.5px] font-medium text-slate-700 mb-1.5">Password</span>
                            <input
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-login-password"
                                className="w-full border border-slate-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-teal-600"
                            />
                        </label>
                    </div>

                    {err && (
                        <div data-testid="admin-login-error" className="mt-4 border-l-2 border-red-500 bg-red-50 text-red-700 px-3 py-2 text-[13px]">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="admin-login-submit"
                        className="btn-base btn-primary w-full mt-6 disabled:opacity-60"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                </form>

                <p className="mt-5 text-[12px] text-slate-400 text-center">
                    Aurea Digital Solutions · Admin access only
                </p>
            </div>
        </div>
    );
};

export default AdminLogin;
